import React, { useState, useEffect, useCallback } from 'react';
import SSHTerminal from '../components/SSHTerminal';

const PROVIDER_COLORS = { aws:'#ff9900', gcp:'#4285f4', azure:'#0078d4', digitalocean:'#0080ff', vultr:'#007bfc', lightnode:'#00c853' };
const PROVIDER_REGIONS = {
  aws:['us-east-1','us-west-2','eu-west-1','ap-south-1','ap-southeast-1'],
  gcp:['us-central1-f','us-east1-b','europe-west1-b','asia-south1-a','asia-southeast1-a'],
  azure:['westus2','eastus','westeurope','southindia','southeastasia','australiaeast'],
  digitalocean:['nyc1','sfo3','lon1','blr1','sgp1'],
  vultr:['ewr','lax','lhr','sgp','blr','syd'],
  lightnode:['hong-kong','singapore','silicon-valley','frankfurt','dubai'],
};
const INSTANCE_TYPES = {
  aws:['t3.micro','t3.small','t3.medium','t3.large'],
  gcp:['e2-micro','e2-small','e2-medium','e2-standard-2'],
  azure:['Standard_B1s','Standard_B1ms','Standard_B2s','Standard_B2ms'],
  digitalocean:['s-1vcpu-1gb','s-1vcpu-2gb','s-2vcpu-2gb','s-2vcpu-4gb'],
  vultr:['vc2-1c-1gb','vc2-1c-2gb','vc2-2c-4gb'],
  lightnode:['ln-1c-2gb','ln-2c-4gb','ln-4c-8gb'],
};
const OS_OPTIONS = ['Ubuntu 22.04','Ubuntu 20.04','Debian 12','CentOS 9','AlmaLinux 9','Rocky Linux 9'];
const STATUS_COLORS = { running:'#10b981', stopped:'#ef4444', pending:'#f59e0b', unknown:'#64748b' };

const token = () => localStorage.getItem('mcp_token');
const PHYSICAL_FORM = { name:'', ipAddress:'', sshPort:'22', username:'root', os:'Ubuntu 22.04', cpu:'', ram:'', storage:'', monthlyCost:'', notes:'' };
const api = async (path, opts={}) => {
  const r = await fetch(path, { headers: { Authorization:`Bearer ${token()}`, 'Content-Type':'application/json' }, ...opts });
  return r.json();
};

export default function Servers() {
  const [servers,    setServers]    = useState([]);
  const [loading,    setLoading]    = useState(true);
  const [modal,      setModal]      = useState(false);
  const [editServer, setEditServer] = useState(null);
  const [filter,     setFilter]     = useState('all');
  const [search,     setSearch]     = useState('');
  const [toast,      setToast]      = useState(null);
  const [stats,      setStats]      = useState(null);
  const [syncing,    setSyncing]    = useState(false);
  const [provisioning, setProvisioning] = useState(false);
  const [physicalModal, setPhysicalModal] = useState(false);
  const [physicalForm,  setPhysicalForm]  = useState(PHYSICAL_FORM);
  const [sshServer, setSSHServer] = useState(null);
  const [provisionMode, setProvisionMode] = useState(false);
  const [form, setForm] = useState({ provider:'gcp', name:'', instanceId:'', ipAddress:'', region:'us-central1-f', size:'e2-micro', os:'Ubuntu 22.04', status:'running', cpu:'1', ram:'1', storage:'20', monthlyCost:'', notes:'' });

  const showToast = (msg, type='success') => { setToast({msg,type}); setTimeout(()=>setToast(null),6000); };

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const [s, st] = await Promise.all([api('/api/servers'), api('/api/servers/stats')]);
      setServers(s.data || []);
      setStats(st.data || null);
    } catch(e) { console.error(e); }
    finally { setLoading(false); }
  }, []);

  useEffect(() => { load(); }, []);
  useEffect(() => {
    const params = new URLSearchParams(window.location.search);
    const sshId = params.get('ssh');
    if (sshId && servers.length > 0) {
      const target = servers.find(s => s._id === sshId);
      if (target) setSSHServer(target);
    }
  }, [servers]);

  const syncFromCloud = async () => {
    setSyncing(true);
    try {
      const res = await api('/api/deployments/sync/all');
      await load();
      const summary = res?.data ? Object.entries(res.data).map(([p,r]) => `${p}: ${r.synced||0}`).join(', ') : '';
      showToast(`Synced all cloud providers! ${summary}`);
    } catch(e) { showToast('Sync failed: ' + e.message, 'error'); }
    finally { setSyncing(false); }
  };

  const openAdd = () => {
    setEditServer(null);
    setProvisionMode(false);
    setForm({ provider:'gcp', name:'', instanceId:'', ipAddress:'', region:'us-central1-f', size:'e2-micro', os:'Ubuntu 22.04', status:'running', cpu:'1', ram:'1', storage:'20', monthlyCost:'', notes:'' });
    setModal(true);
  };

  const openEdit = (s) => {
    setEditServer(s);
    setProvisionMode(false);
    setForm({ provider:s.provider, name:s.name, instanceId:s.instanceId||'', ipAddress:s.ipAddress||'', region:s.region||'', size:s.size||'', os:s.os||'', status:s.status||'running', cpu:s.cpu||'', ram:s.ram||'', storage:s.storage||'', monthlyCost:s.monthlyCost||'', notes:s.notes||'' });
    setModal(true);
  };

  const handleSubmit = async e => {
    e.preventDefault();
    if (provisioning) return;
    try {
      if (editServer) {
        await api(`/api/servers/${editServer._id}`, { method:'PUT', body: JSON.stringify(form) });
        showToast('Server updated!');
        setModal(false);
        load();
      } else if (provisionMode) {
        // Actually provision on real cloud
        setProvisioning(true);
        showToast(`⏳ Provisioning ${form.provider.toUpperCase()} VM... this may take 1-3 minutes`, 'success');
        const r = await api('/api/servers', {
          method: 'POST',
          body: JSON.stringify({ ...form, provisionOnCloud: true }),
        });
        setProvisioning(false);
        if (r.success) {
          showToast(r.message || `✅ VM provisioned on ${form.provider.toUpperCase()}!`);
          setModal(false);
          load();
          setTimeout(load, 15000);
          setTimeout(load, 60000);
        } else {
          showToast(r.error || 'Provisioning failed', 'error');
        }
      } else {
        // Manual tracking entry only
        const r = await api('/api/servers', { method:'POST', body: JSON.stringify(form) });
        if (!r.success) { showToast(r.error || 'Failed', 'error'); return; }
        showToast('Server added to tracking!');
        setModal(false);
        load();
      }
    } catch(err) {
      setProvisioning(false);
      showToast('Failed: ' + err.message, 'error');
    }
  };

  const handleDelete = async (s) => {
    if (!window.confirm(`Remove "${s.name}"?`)) return;
    await api(`/api/servers/${s._id}`, { method:'DELETE' });
    showToast('Server removed');
    load();
  };

  const filtered = servers.filter(s =>
    (filter === 'all' || s.provider === filter) &&
    (s.name?.toLowerCase().includes(search.toLowerCase()) ||
     (s.ipAddress||'').includes(search) ||
     (s.instanceId||'').toLowerCase().includes(search.toLowerCase()))
  );

  const currentRegions = PROVIDER_REGIONS[form.provider] || [];
  const currentTypes   = INSTANCE_TYPES[form.provider]   || [];

  return (
    <div>
      {toast && (
        <div style={{position:'fixed',top:20,right:20,zIndex:9999,background:toast.type==='success'?'rgba(16,185,129,0.15)':'rgba(239,68,68,0.15)',border:`1px solid ${toast.type==='success'?'#10b981':'#ef4444'}`,borderRadius:10,padding:'12px 20px',color:toast.type==='success'?'#10b981':'#ef4444',fontWeight:600,backdropFilter:'blur(10px)',maxWidth:400}}>
          {toast.type==='success'?'✅':'❌'} {toast.msg}
        </div>
      )}

      <div className="page-header" style={{marginBottom:24}}>
        <div>
          <h1 style={{margin:0}}>Servers</h1>
          <p style={{color:'#8899b4',margin:'4px 0 0'}}>Manage your cloud servers and instances</p>
        </div>
        <div style={{display:'flex',gap:10}}>
          <button onClick={() => setPhysicalModal(true)} style={{padding:'10px 18px',borderRadius:9,background:'rgba(16,185,129,0.15)',border:'1px solid rgba(16,185,129,0.4)',color:'#10b981',fontWeight:700,cursor:'pointer',fontSize:13}}>🖥️ Add Physical Server</button>
          <button onClick={syncFromCloud} disabled={syncing} style={{padding:'10px 18px',borderRadius:9,background:'rgba(99,102,241,0.15)',border:'1px solid rgba(99,102,241,0.4)',color:'#6366f1',fontWeight:700,cursor:'pointer',fontSize:13}}>
            {syncing ? '⏳ Syncing...' : '🔄 Sync from Cloud'}
          </button>
          <button onClick={openAdd} style={{padding:'10px 20px',borderRadius:9,background:'#6366f1',border:'none',color:'#fff',fontWeight:700,cursor:'pointer',fontSize:14}}>+ Add Server</button>
        </div>
      </div>

      {stats && (
        <div style={{display:'grid',gridTemplateColumns:'repeat(auto-fit,minmax(160px,1fr))',gap:14,marginBottom:24}}>
          {[
            {label:'Total Servers', value:stats.total,    color:'#6366f1', icon:'🖥️'},
            {label:'Running',       value:stats.running,  color:'#10b981', icon:'✅'},
            {label:'Stopped',       value:stats.stopped,  color:'#ef4444', icon:'⛔'},
            {label:'Monthly Cost',  value:`$${(stats.totalCost||0).toFixed(2)}`, color:'#f59e0b', icon:'💰'},
          ].map(s => (
            <div key={s.label} style={{background:'var(--bg-card)',borderRadius:12,padding:'16px',border:'1px solid var(--border)'}}>
              <div style={{fontSize:22,marginBottom:6}}>{s.icon}</div>
              <div style={{fontSize:24,fontWeight:800,color:s.color}}>{s.value}</div>
              <div style={{fontSize:12,color:'#8899b4',marginTop:2}}>{s.label}</div>
            </div>
          ))}
        </div>
      )}

      <div style={{display:'flex',gap:10,marginBottom:20,flexWrap:'wrap',alignItems:'center'}}>
        <input type="text" placeholder="🔍 Search servers..." value={search} onChange={e=>setSearch(e.target.value)}
          style={{padding:'8px 14px',borderRadius:8,background:'var(--bg-card)',border:'1px solid var(--border)',color:'var(--text-primary)',fontSize:13,outline:'none',minWidth:200}} />
        {['all',...Object.keys(PROVIDER_COLORS)].map(p => (
          <button key={p} onClick={()=>setFilter(p)} style={{padding:'6px 14px',borderRadius:20,border:filter===p?`1px solid ${p==='all'?'#6366f1':PROVIDER_COLORS[p]}`:'1px solid var(--border)',background:filter===p?`${p==='all'?'rgba(99,102,241,0.15)':`${PROVIDER_COLORS[p]}22`}`:'var(--bg-card)',color:filter===p?(p==='all'?'#6366f1':PROVIDER_COLORS[p]):'#8899b4',fontSize:12,fontWeight:600,cursor:'pointer',textTransform:'uppercase'}}>
            {p === 'all' ? 'ALL' : p.toUpperCase()}
          </button>
        ))}
      </div>

      {loading ? (
        <div className="loading"><div className="spinner"/></div>
      ) : filtered.length === 0 ? (
        <div style={{textAlign:'center',padding:'60px 20px',color:'#4d6080'}}>
          <div style={{fontSize:48,marginBottom:12}}>🖥️</div>
          <div style={{fontSize:16,fontWeight:600,color:'#8899b4',marginBottom:8}}>No servers found</div>
          <div style={{display:'flex',gap:12,justifyContent:'center'}}>
            <button onClick={syncFromCloud} style={{padding:'10px 20px',borderRadius:9,background:'rgba(99,102,241,0.15)',border:'1px solid rgba(99,102,241,0.4)',color:'#6366f1',fontWeight:700,cursor:'pointer'}}>🔄 Sync from Cloud</button>
            <button onClick={openAdd} style={{padding:'10px 20px',borderRadius:9,background:'#6366f1',border:'none',color:'#fff',fontWeight:700,cursor:'pointer'}}>+ Add Server</button>
          </div>
        </div>
      ) : (
        <div style={{display:'grid',gridTemplateColumns:'repeat(auto-fill,minmax(320px,1fr))',gap:16}}>
          {filtered.map(s => {
            const color = PROVIDER_COLORS[s.provider] || '#6366f1';
            return (
              <div key={s._id} style={{background:'var(--bg-card)',borderRadius:14,padding:18,border:`1px solid var(--border)`,borderTop:`3px solid ${color}`}}>
                <div style={{display:'flex',justifyContent:'space-between',alignItems:'flex-start',marginBottom:12}}>
                  <div>
                    <div style={{fontWeight:700,fontSize:15,marginBottom:4}}>{s.name}</div>
                    <div style={{display:'flex',gap:6,flexWrap:'wrap'}}>
                      <span style={{fontSize:10,fontFamily:'monospace',background:`${color}22`,color:color,padding:'2px 8px',borderRadius:4,fontWeight:700,textTransform:'uppercase'}}>{s.provider}</span>
                      {s.region && <span style={{fontSize:10,fontFamily:'monospace',background:'var(--bg-secondary)',color:'#8899b4',padding:'2px 8px',borderRadius:4}}>{s.region}</span>}
                    </div>
                  </div>
                  <div style={{display:'flex',alignItems:'center',gap:5,fontSize:11,fontWeight:700,padding:'3px 10px',borderRadius:12,background:`${STATUS_COLORS[s.status]||'#64748b'}22`,color:STATUS_COLORS[s.status]||'#64748b',border:`1px solid ${STATUS_COLORS[s.status]||'#64748b'}44`}}>
                    <div style={{width:6,height:6,borderRadius:'50%',background:STATUS_COLORS[s.status]||'#64748b'}}/>
                    {s.status}
                  </div>
                </div>
                <div style={{display:'grid',gridTemplateColumns:'1fr 1fr',gap:8,marginBottom:12}}>
                  {s.ipAddress && <div style={{background:'var(--bg-secondary)',borderRadius:7,padding:'8px 10px'}}><div style={{fontSize:10,color:'#4d6080',marginBottom:2}}>IP ADDRESS</div><div style={{fontSize:12,fontFamily:'monospace',color:'#8899b4'}}>{s.ipAddress}</div></div>}
                  {s.os        && <div style={{background:'var(--bg-secondary)',borderRadius:7,padding:'8px 10px'}}><div style={{fontSize:10,color:'#4d6080',marginBottom:2}}>OS</div><div style={{fontSize:12,color:'#8899b4'}}>{s.os}</div></div>}
                  {s.size      && <div style={{background:'var(--bg-secondary)',borderRadius:7,padding:'8px 10px'}}><div style={{fontSize:10,color:'#4d6080',marginBottom:2}}>SIZE</div><div style={{fontSize:12,color:'#8899b4'}}>{s.size}</div></div>}
                  {s.monthlyCost > 0 && <div style={{background:'var(--bg-secondary)',borderRadius:7,padding:'8px 10px'}}><div style={{fontSize:10,color:'#4d6080',marginBottom:2}}>MONTHLY</div><div style={{fontSize:12,fontFamily:'monospace',color:'#f59e0b',fontWeight:700}}>${s.monthlyCost}/mo</div></div>}
                </div>
                {(s.cpu > 0 || s.ram > 0 || s.storage > 0) && (
                  <div style={{display:'flex',gap:10,marginBottom:12}}>
                    {s.cpu>0     && <div style={{flex:1,textAlign:'center',background:'var(--bg-secondary)',borderRadius:7,padding:'6px'}}><div style={{fontSize:16,fontWeight:800,color}}>{s.cpu}</div><div style={{fontSize:9,color:'#4d6080'}}>vCPU</div></div>}
                    {s.ram>0     && <div style={{flex:1,textAlign:'center',background:'var(--bg-secondary)',borderRadius:7,padding:'6px'}}><div style={{fontSize:16,fontWeight:800,color}}>{s.ram}GB</div><div style={{fontSize:9,color:'#4d6080'}}>RAM</div></div>}
                    {s.storage>0 && <div style={{flex:1,textAlign:'center',background:'var(--bg-secondary)',borderRadius:7,padding:'6px'}}><div style={{fontSize:16,fontWeight:800,color}}>{s.storage}GB</div><div style={{fontSize:9,color:'#4d6080'}}>DISK</div></div>}
                  </div>
                )}
                {s.instanceId && <div style={{fontSize:11,fontFamily:'monospace',color:'#4d6080',marginBottom:12,background:'var(--bg-secondary)',padding:'6px 10px',borderRadius:6,wordBreak:'break-all'}}>ID: {s.instanceId}</div>}
                {s.notes      && <div style={{fontSize:12,color:'#8899b4',marginBottom:12,fontStyle:'italic'}}>"{s.notes}"</div>}
                <div style={{display:'flex',gap:8}}>
                  <button onClick={()=>setSSHServer(s)} style={{flex:1,padding:'7px',borderRadius:7,background:'rgba(16,185,129,0.08)',border:'1px solid rgba(16,185,129,0.25)',color:'#10b981',fontSize:12,fontWeight:600,cursor:'pointer'}}>🖥️ SSH</button>
                  <button onClick={()=>openEdit(s)} style={{flex:1,padding:'7px',borderRadius:7,background:`${color}15`,border:`1px solid ${color}35`,color,fontSize:12,fontWeight:600,cursor:'pointer'}}>✏️ Edit</button>
                  <button onClick={()=>handleDelete(s)} style={{flex:1,padding:'7px',borderRadius:7,background:'rgba(239,68,68,0.08)',border:'1px solid rgba(239,68,68,0.25)',color:'#ef4444',fontSize:12,fontWeight:600,cursor:'pointer'}}>🗑️ Remove</button>
                </div>
              </div>
            );
          })}
        </div>
      )}

      {/* Add/Edit Modal */}
      {modal && (
        <div style={{position:'fixed',inset:0,background:'rgba(0,0,0,0.75)',display:'flex',alignItems:'center',justifyContent:'center',zIndex:1000,padding:20}} onClick={()=>!provisioning&&setModal(false)}>
          <div style={{background:'var(--bg-card)',borderRadius:16,padding:28,width:'100%',maxWidth:540,border:'1px solid var(--border)',maxHeight:'90vh',overflowY:'auto'}} onClick={e=>e.stopPropagation()}>
            <div style={{display:'flex',alignItems:'center',justifyContent:'space-between',marginBottom:16}}>
              <h2 style={{margin:0,fontSize:18}}>{editServer ? '✏️ Edit Server' : '➕ Add Server'}</h2>
              <button onClick={()=>!provisioning&&setModal(false)} style={{background:'none',border:'none',color:'#8899b4',fontSize:20,cursor:'pointer'}}>✕</button>
            </div>

            {!editServer && (
              <div style={{display:'flex',gap:10,marginBottom:20,padding:14,background:'var(--bg-secondary)',borderRadius:10,border:'1px solid var(--border)'}}>
                <button onClick={()=>setProvisionMode(false)} style={{flex:1,padding:'8px',borderRadius:7,background:!provisionMode?'#6366f1':'transparent',border:!provisionMode?'none':'1px solid var(--border)',color:!provisionMode?'#fff':'#8899b4',fontWeight:600,cursor:'pointer',fontSize:12}}>
                  📋 Track Only
                </button>
                <button onClick={()=>setProvisionMode(true)} style={{flex:1,padding:'8px',borderRadius:7,background:provisionMode?'#10b981':'transparent',border:provisionMode?'none':'1px solid var(--border)',color:provisionMode?'#fff':'#8899b4',fontWeight:600,cursor:'pointer',fontSize:12}}>
                  🚀 Provision on Cloud
                </button>
              </div>
            )}

            {!editServer && (
              <div style={{marginBottom:14,padding:'10px 14px',borderRadius:8,background:provisionMode?'rgba(16,185,129,0.1)':'rgba(99,102,241,0.1)',border:`1px solid ${provisionMode?'rgba(16,185,129,0.3)':'rgba(99,102,241,0.3)'}`,fontSize:12,color:provisionMode?'#10b981':'#6366f1'}}>
                {provisionMode
                  ? '🚀 This will create a REAL VM on the selected cloud provider. Takes 1-3 minutes.'
                  : '📋 This adds the server to your tracking dashboard only. No VM will be created on the cloud.'}
              </div>
            )}

            <form onSubmit={handleSubmit}>
              <div style={{display:'grid',gridTemplateColumns:'1fr 1fr',gap:14}}>
                <div style={{gridColumn:'1/-1'}}>
                  <label style={{fontSize:13,color:'#8899b4',display:'block',marginBottom:6}}>Cloud Provider *</label>
                  <select className="form-control" value={form.provider} onChange={e=>setForm({...form,provider:e.target.value,region:PROVIDER_REGIONS[e.target.value]?.[0]||'',size:INSTANCE_TYPES[e.target.value]?.[0]||''})} required style={{background:'var(--bg-secondary)',color:'var(--text-primary)'}}>
                    {Object.keys(PROVIDER_COLORS).map(p=><option key={p} value={p}>{p.toUpperCase()}</option>)}
                  </select>
                </div>
                <div style={{gridColumn:'1/-1'}}>
                  <label style={{fontSize:13,color:'#8899b4',display:'block',marginBottom:6}}>Server Name *</label>
                  <input className="form-control" placeholder="my-server-01" value={form.name} onChange={e=>setForm({...form,name:e.target.value.toLowerCase().replace(/[^a-z0-9-]/g,'-')})} required/>
                </div>
                <div>
                  <label style={{fontSize:13,color:'#8899b4',display:'block',marginBottom:6}}>Region</label>
                  <select className="form-control" value={form.region} onChange={e=>setForm({...form,region:e.target.value})} style={{background:'var(--bg-secondary)',color:'var(--text-primary)'}}>
                    <option value="">Select region...</option>
                    {currentRegions.map(r=><option key={r} value={r}>{r}</option>)}
                  </select>
                </div>
                <div>
                  <label style={{fontSize:13,color:'#8899b4',display:'block',marginBottom:6}}>{provisionMode ? 'Instance Type' : 'Size'}</label>
                  {provisionMode ? (
                    <select className="form-control" value={form.size} onChange={e=>setForm({...form,size:e.target.value})} style={{background:'var(--bg-secondary)',color:'var(--text-primary)'}}>
                      {currentTypes.map(t=><option key={t} value={t}>{t}</option>)}
                    </select>
                  ) : (
                    <input className="form-control" placeholder="e2-micro" value={form.size} onChange={e=>setForm({...form,size:e.target.value})}/>
                  )}
                </div>
                {!provisionMode && (
                  <>
                    <div>
                      <label style={{fontSize:13,color:'#8899b4',display:'block',marginBottom:6}}>Instance ID</label>
                      <input className="form-control" placeholder="i-0abc123..." value={form.instanceId} onChange={e=>setForm({...form,instanceId:e.target.value})}/>
                    </div>
                    <div>
                      <label style={{fontSize:13,color:'#8899b4',display:'block',marginBottom:6}}>IP Address</label>
                      <input className="form-control" placeholder="192.168.1.100" value={form.ipAddress} onChange={e=>setForm({...form,ipAddress:e.target.value})}/>
                    </div>
                    <div>
                      <label style={{fontSize:13,color:'#8899b4',display:'block',marginBottom:6}}>Status</label>
                      <select className="form-control" value={form.status} onChange={e=>setForm({...form,status:e.target.value})} style={{background:'var(--bg-secondary)',color:'var(--text-primary)'}}>
                        <option value="running">Running</option>
                        <option value="stopped">Stopped</option>
                        <option value="pending">Pending</option>
                        <option value="unknown">Unknown</option>
                      </select>
                    </div>
                    <div>
                      <label style={{fontSize:13,color:'#8899b4',display:'block',marginBottom:6}}>Monthly Cost ($)</label>
                      <input className="form-control" type="number" placeholder="6.05" value={form.monthlyCost} onChange={e=>setForm({...form,monthlyCost:e.target.value})}/>
                    </div>
                    <div><label style={{fontSize:13,color:'#8899b4',display:'block',marginBottom:6}}>vCPU</label><input className="form-control" type="number" placeholder="1" value={form.cpu} onChange={e=>setForm({...form,cpu:e.target.value})}/></div>
                    <div><label style={{fontSize:13,color:'#8899b4',display:'block',marginBottom:6}}>RAM (GB)</label><input className="form-control" type="number" placeholder="1" value={form.ram} onChange={e=>setForm({...form,ram:e.target.value})}/></div>
                  </>
                )}
                <div style={{gridColumn:'1/-1'}}>
                  <label style={{fontSize:13,color:'#8899b4',display:'block',marginBottom:6}}>OS</label>
                  <select className="form-control" value={form.os} onChange={e=>setForm({...form,os:e.target.value})} style={{background:'var(--bg-secondary)',color:'var(--text-primary)'}}>
                    {OS_OPTIONS.map(o=><option key={o} value={o}>{o}</option>)}
                  </select>
                </div>
                {!provisionMode && (
                  <div style={{gridColumn:'1/-1'}}>
                    <label style={{fontSize:13,color:'#8899b4',display:'block',marginBottom:6}}>Notes</label>
                    <textarea className="form-control" rows={2} placeholder="Any notes..." value={form.notes} onChange={e=>setForm({...form,notes:e.target.value})} style={{resize:'vertical'}}/>
                  </div>
                )}
              </div>
              <div style={{display:'flex',gap:10,marginTop:20}}>
                <button type="button" onClick={()=>!provisioning&&setModal(false)} style={{flex:1,padding:'10px',borderRadius:8,background:'var(--bg-secondary)',border:'1px solid var(--border)',color:'#8899b4',cursor:'pointer',fontWeight:600}} disabled={provisioning}>Cancel</button>
                <button type="submit" disabled={provisioning} style={{flex:2,padding:'10px',borderRadius:8,background:provisionMode?'#10b981':'#6366f1',border:'none',color:'#fff',fontWeight:700,cursor:provisioning?'wait':'pointer',fontSize:14}}>
                  {provisioning ? '⏳ Provisioning VM on cloud...' : editServer ? '✏️ Update Server' : provisionMode ? '🚀 Provision on Cloud' : '➕ Add to Tracking'}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

      {/* Physical Server Modal */}
      {sshServer && <SSHTerminal server={sshServer} onClose={()=>setSSHServer(null)} />}

      {physicalModal && (
        <div style={{position:'fixed',inset:0,background:'rgba(0,0,0,0.75)',display:'flex',alignItems:'center',justifyContent:'center',zIndex:1000,padding:20}} onClick={()=>setPhysicalModal(false)}>
          <div style={{background:'var(--bg-card)',borderRadius:16,padding:28,width:'100%',maxWidth:480,border:'1px solid var(--border)'}} onClick={e=>e.stopPropagation()}>
            <div style={{display:'flex',alignItems:'center',justifyContent:'space-between',marginBottom:20}}>
              <h2 style={{margin:0,fontSize:18}}>🖥️ Add Physical Server</h2>
              <button onClick={()=>setPhysicalModal(false)} style={{background:'none',border:'none',color:'#8899b4',fontSize:20,cursor:'pointer'}}>✕</button>
            </div>
            <form onSubmit={async e => {
              e.preventDefault();
              const r = await api('/api/servers/physical', { method:'POST', body: JSON.stringify(physicalForm) });
              if (r.success) { showToast('Physical server added!'); setPhysicalModal(false); setPhysicalForm(PHYSICAL_FORM); load(); }
              else showToast(r.error || 'Failed', 'error');
            }}>
              <div style={{display:'grid',gridTemplateColumns:'1fr 1fr',gap:14}}>
                <div style={{gridColumn:'1/-1'}}><label style={{fontSize:13,color:'#8899b4',display:'block',marginBottom:6}}>Server Name *</label><input className="form-control" placeholder="my-server" value={physicalForm.name} onChange={e=>setPhysicalForm({...physicalForm,name:e.target.value})} required/></div>
                <div><label style={{fontSize:13,color:'#8899b4',display:'block',marginBottom:6}}>IP Address *</label><input className="form-control" placeholder="192.168.1.1" value={physicalForm.ipAddress} onChange={e=>setPhysicalForm({...physicalForm,ipAddress:e.target.value})} required/></div>
                <div><label style={{fontSize:13,color:'#8899b4',display:'block',marginBottom:6}}>SSH Port</label><input className="form-control" placeholder="22" value={physicalForm.sshPort} onChange={e=>setPhysicalForm({...physicalForm,sshPort:e.target.value})}/></div>
                <div><label style={{fontSize:13,color:'#8899b4',display:'block',marginBottom:6}}>Username</label><input className="form-control" placeholder="root" value={physicalForm.username} onChange={e=>setPhysicalForm({...physicalForm,username:e.target.value})}/></div>
                <div><label style={{fontSize:13,color:'#8899b4',display:'block',marginBottom:6}}>Monthly Cost ($)</label><input className="form-control" type="number" placeholder="0" value={physicalForm.monthlyCost} onChange={e=>setPhysicalForm({...physicalForm,monthlyCost:e.target.value})}/></div>
              </div>
              <div style={{display:'flex',gap:10,marginTop:20}}>
                <button type="button" onClick={()=>setPhysicalModal(false)} style={{flex:1,padding:'10px',borderRadius:8,background:'var(--bg-secondary)',border:'1px solid var(--border)',color:'#8899b4',cursor:'pointer',fontWeight:600}}>Cancel</button>
                <button type="submit" style={{flex:2,padding:'10px',borderRadius:8,background:'#10b981',border:'none',color:'#fff',fontWeight:700,cursor:'pointer',fontSize:14}}>🖥️ Add Physical Server</button>
              </div>
            </form>
          </div>
        </div>
      )}
    </div>
  );
}
